'use client'

import { useState, type CSSProperties } from 'react'
import { Cookie, Shield, Eye, Settings, Clock, Trash2 } from 'lucide-react'
import Link from 'next/link'
import { LegalHero, RefPlus } from '@/components/reference/misc/kit'
import { reopenConsentBanner } from '@/lib/consent-manager'

export default function CookiePolicyPage() {
  const [openSections, setOpenSections] = useState<number[]>([0]) // First section open by default

  const toggleSection = (index: number) => {
    setOpenSections(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    )
  }

  const sections = [
    {
      title: 'What Are Cookies and Browser Storage',
      icon: Cookie,
      content: (
        <>
          <p>
            Cookies are small text files that websites place on your device to
            remember information between visits. Modern websites, including{' '}
            <a href='https://www.pixelmojo.io' className='lg-a'>
              pixelmojo.io
            </a>
            , also use browser storage (called &quot;local storage&quot;), which
            works like cookies but stays on your device and is never sent
            automatically with every request.
          </p>
          <p>
            This Cookie Policy explains exactly what we store on your device,
            what is strictly necessary, what is optional, and how you stay in
            control. For how we handle personal information more broadly, see
            our{' '}
            <Link href='/privacy-policy' className='lg-a'>
              Privacy Policy
            </Link>
            .
          </p>
          <p>
            We do not use advertising cookies, remarketing cookies, or any
            cross-site tracking. The advertising signals in our consent system
            are permanently set to &quot;denied&quot; and never change,
            regardless of what you choose.
          </p>
        </>
      ),
    },
    {
      title: 'Strictly Necessary Storage',
      icon: Shield,
      content: (
        <>
          <p>
            Some storage is required for the site to work and does not need
            consent. It is used only to provide features you actively use, never
            for tracking:
          </p>
          <div className='lg-stack'>
            {[
              {
                name: 'Your cookie consent choice',
                desc: 'When you make a choice on the banner (Accept All, Reject All, Got It, or Opt Out), we save your decision in browser storage (under the keys pixelmojo_cookie_consent_v2 and pm_consent_v2) along with the date you decided. This is how the site remembers not to ask you again on every page, and how it knows whether analytics may run.',
              },
              {
                name: 'Region consent policy',
                desc: "A cookie named pm_region_policy remembers which consent rules apply where you are: either 'prior-consent' or 'notice-opt-out' (see the Optional Analytics section). It stores only that policy label, never your country or any identifier, and it refreshes on every visit (it lasts at most 24 hours).",
              },
              {
                name: 'Theme preference',
                desc: 'Remembers whether you chose light or dark mode.',
              },
              {
                name: 'Radar sign-in session',
                desc: 'If you sign in to Radar, your access token is kept in browser storage so you stay signed in between pages. Without it, sign-in would not work.',
              },
              {
                name: 'Chat session',
                desc: 'If you use our AI chat, an anonymous session identifier is stored so the conversation can continue across pages. It is used only by the chat feature.',
              },
              {
                name: 'Free-tool usage',
                desc: 'Our free audit tools store an anonymous marker on your device to apply the free-tier limit fairly. It is not derived from any personal identifier.',
              },
            ].map((item, i) => (
              <div key={i} className='lg-cell'>
                <p className='lg-strong'>{item.name}</p>
                <p className='lg-sm lg-muted'>{item.desc}</p>
              </div>
            ))}
          </div>
          <p>
            All of this stays on your device. None of it is used for analytics
            or shared with advertisers.
          </p>
        </>
      ),
    },
    {
      title: 'Optional Analytics',
      icon: Eye,
      content: (
        <>
          <p>
            Depending on your location, we apply different requirements to the
            optional analytics services below. Where applicable law requires
            prior consent, including the <strong>EEA</strong>, the{' '}
            <strong>United Kingdom</strong>, and <strong>Switzerland</strong>,
            they stay off until you press <strong>Accept All</strong>: no
            scripts, no requests, no cookies. If you press Reject All, or simply
            never decide, they do not load at all. If we cannot determine your
            location, we apply this same prior-consent rule.
          </p>
          <p>
            In other regions, these analytics services may start when you access
            the site. The banner tells you this and offers an{' '}
            <strong>Opt Out</strong> button, and you can opt out at any time
            through the Cookie Settings link in the footer. A previously
            recorded rejection or opt-out is <strong>always respected</strong>,
            regardless of the regional default.
          </p>
          <div className='lg-stack'>
            {[
              {
                name: 'Google Analytics 4 (gtag.js)',
                desc: 'Helps us understand which pages are visited and how the site is used. Sets Google Analytics cookies (such as _ga) only when analytics is permitted for you, through your acceptance or the regional default described above. We use Google Consent Mode v2 with all advertising signals permanently denied, and analytics is measured without any ad personalization.',
                url: 'https://policies.google.com/privacy',
              },
              {
                name: 'Microsoft Clarity',
                desc: 'Provides heatmaps and interaction analytics so we can improve usability. Loads and sets its cookies only when analytics is permitted for you, and receives its own explicit consent signal from us.',
                url: 'https://privacy.microsoft.com/privacystatement',
              },
              {
                name: 'PostHog',
                desc: 'Product analytics for our Radar platform. Records a limited set of product events (for example, that an audit was started or a report was exported) and page views, only when analytics is permitted for you. It sets no cookies (it uses browser storage only), we never tell it who you are, and autocapture, session recording, heatmaps, and surveys are all switched off.',
                url: 'https://posthog.com/privacy',
              },
            ].map((service, i) => (
              <div key={i} className='lg-cell'>
                <div className='lg-split'>
                  <div>
                    <h4 className='lg-strong'>{service.name}</h4>
                    <p className='lg-sm lg-muted'>{service.desc}</p>
                  </div>
                  <a
                    href={service.url}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='lg-a lg-xs lg-nowrap'
                  >
                    Privacy Policy →
                  </a>
                </div>
              </div>
            ))}
          </div>

          <h3 className='lg-h'>
            <span className='lg-accent'>→</span> Our Own Journey Identifier
          </h3>
          <p>
            When analytics is permitted for you, we also store one first-party
            identifier of our own: a random code in your browser&apos;s local
            storage named <strong>pm_visitor_id</strong>. It is not a cookie and
            it is not derived from anything about you. We do not send it to
            PostHog, Google, Microsoft or any advertising platform. We do send
            it to Paddle, our payment provider, when you check out. When you
            verify an email for a free tool, pay, or run a signed-in paid audit,
            our database links it to a hashed copy of that email address and the
            time of each step. If you arrived from an AI assistant, it is also
            linked to which assistant sent you, the referring address, the page
            you landed on and any campaign tags. We use this only to see how
            visitors become customers, never to decide access, credits or which
            emails you receive. If analytics is never permitted for you, the
            identifier is never created. If you withdraw consent later, your
            browser deletes it and asks us to delete the linked record, retrying
            on later visits until we confirm. We then keep only the random
            identifier and the time you withdrew, so nothing new is linked to
            it. Journey records are deleted 13 months after we first saw the
            identifier. Payment records are kept for accounting, and withdrawing
            consent does not remove the identifier from Paddle&apos;s record of
            your payment or from our copy of Paddle&apos;s payment notification.
          </p>

          <h3 className='lg-h'>
            <span className='lg-accent'>→</span> Always-On, Cookieless Services
          </h3>
          <p>
            A few services run without consent because they set no cookies and
            do not identify you:
          </p>
          <div className='lg-stack'>
            {[
              {
                name: 'Vercel Analytics',
                desc: 'Anonymous, cookieless performance measurement (page counts and loading speed). It sets no cookies, stores nothing on your device, and cannot identify you across sites or visits.',
              },
              {
                name: 'Sentry',
                desc: 'Error diagnostics. If something breaks, a technical error report helps us fix it. It sets no cookies, does not record your session ambiently, and error reports are configured to exclude personal information.',
              },
              {
                name: 'Paddle',
                desc: 'Our payment provider. Paddle only comes into play when you open a checkout, where it may use cookies strictly for processing the payment and preventing fraud. It is never present during normal browsing.',
              },
            ].map((item, i) => (
              <div key={i} className='lg-cell'>
                <p className='lg-strong'>{item.name}</p>
                <p className='lg-sm lg-muted'>{item.desc}</p>
              </div>
            ))}
          </div>
        </>
      ),
    },
    {
      title: 'How Consent Works',
      icon: Settings,
      content: (
        <>
          <p>
            The first time you visit, a banner appears. What it asks depends on
            where you are:
          </p>
          <div className='lg-grid'>
            {[
              {
                label: 'Where prior consent is required (EEA, UK, Switzerland)',
                desc: 'Analytics stays off until you press Accept All. Reject All keeps it off. Ignoring or dismissing the banner is treated the same as rejecting. Advertising signals stay denied either way.',
              },
              {
                label: 'Everywhere else',
                desc: 'Analytics is active by default and the banner says so. Got It acknowledges the notice; Opt Out switches analytics off immediately and is remembered. Advertising signals stay denied either way.',
              },
            ].map((item, i) => (
              <div key={i} className='lg-note'>
                <p className='lg-sm lg-strong'>{item.label}</p>
                <p className='lg-xs lg-muted'>{item.desc}</p>
              </div>
            ))}
          </div>
          <p>
            Whichever region you are in, a saved rejection or opt-out always
            wins over any regional default, and a saved acceptance is honored
            wherever you browse from.
          </p>
          <p>
            You can change your mind at any time. The{' '}
            <strong>Cookie Settings</strong> link in the footer of every page
            reopens the consent banner, or you can use the button below:
          </p>
          <button
            type='button'
            onClick={reopenConsentBanner}
            className='btn btn--accent btn--sm'
          >
            Open Cookie Settings
          </button>

          <h3 className='lg-h'>
            <span className='lg-accent'>→</span> How We Measure the Banner
            Itself
          </h3>
          <p>
            To verify that these consent controls actually work, we keep a small
            first-party record of banner activity. Each record contains exactly
            three things: the type of event (banner_shown, accepted, rejected,
            opted_out, preference_changed, or policy_applied), which regional
            policy applied (prior-consent or notice-opt-out), and the time our
            server received it.
          </p>
          <p>
            These records contain no email address, no account or session
            identifier, no IP address, no browser details, no page URL, and no
            free text, so they cannot be linked to you. We use them only to
            check that the banner works, to measure whether visitors can
            successfully make privacy choices, and to diagnose failures in the
            consent interface. They are not user-level tracking.
          </p>
        </>
      ),
    },
    {
      title: 'How Long Your Choice Lasts',
      icon: Clock,
      content: (
        <>
          <p>
            Your consent decision is saved with the date you made it and is
            valid for <strong>one year</strong>. After a year, your saved choice
            expires and the banner will ask you again.
          </p>
          <p>
            Changing your decision through Cookie Settings replaces the saved
            choice immediately and restarts the one-year period.
          </p>
          <p>
            This applies to decisions you actively make. The regional default
            described in the Optional Analytics section is not a saved decision;
            it simply applies until you decide, and your decision then takes
            priority.
          </p>
        </>
      ),
    },
    {
      title: 'Clearing Stored Preferences',
      icon: Trash2,
      content: (
        <>
          <p>
            You can remove everything this site stores on your device through
            your browser. Look for &quot;Clear browsing data&quot; or
            &quot;Cookies and site data&quot; in your browser settings and clear
            data for pixelmojo.io:
          </p>
          <div className='lg-cell lg-stack'>
            {[
              'Chrome: Settings → Privacy and security → Delete browsing data',
              'Safari: Settings → Privacy → Manage Website Data',
              'Firefox: Settings → Privacy & Security → Cookies and Site Data',
              'Edge: Settings → Cookies and site permissions → Manage and delete cookies',
            ].map((item, i) => (
              <div key={i} className='lg-row lg-row--tight'>
                <span className='lg-accent'>•</span>
                <span className='lg-sm lg-muted'>{item}</span>
              </div>
            ))}
          </div>
          <p>
            Clearing site data removes your consent choice along with any
            sign-in session and preferences, so the cookie banner will appear
            again on your next visit. Until you decide again, the regional rule
            applies: analytics stays off where prior consent is required, and
            runs by default (with the opt-out offered) elsewhere.
          </p>
        </>
      ),
    },
  ]

  return (
    <div className='shell pg-blog pg-legal'>
      <section className='sheet bhero bpage' id='top' data-rail='Cookies'>
        <div className='container'>
          <LegalHero
            eyebrow='Legal'
            title='Cookie Policy'
            description='What we store on your device, what is optional, and how you stay in control. Last updated: September 25, 2026'
            ctaText='Contact Us'
            ctaHref='/contact-us'
          />

          <div className='barticle__grid'>
            {/* Quick Navigation */}
            <aside className='bside'>
              <nav className='lg-toc' aria-label='Quick Navigation'>
                <h2 className='lg-toc__title'>Quick Navigation</h2>
                <div className='lg-toc__list'>
                  {sections.map((section, i) => (
                    <button
                      key={i}
                      type='button'
                      onClick={() => toggleSection(i)}
                      aria-pressed={openSections.includes(i)}
                      className='lg-toc__btn'
                    >
                      <span>{section.title}</span>
                    </button>
                  ))}
                </div>
              </nav>
            </aside>

            {/* Accordion Sections */}
            <article className='post'>
              <div className='lg-secs'>
                {sections.map((section, index) => {
                  const isOpen = openSections.includes(index)

                  return (
                    <div key={index} className='lg-sec' data-open={isOpen}>
                      <button
                        type='button'
                        onClick={() => toggleSection(index)}
                        aria-controls={`cookies-section-${index}`}
                        aria-expanded={isOpen}
                        className='lg-sec__head'
                      >
                        <div className='lg-sec__title'>
                          <span className='lg-sec__n'>Section {index + 1}</span>
                          <h3>{section.title}</h3>
                        </div>
                        <span className='lg-plus' aria-hidden='true'>
                          <RefPlus />
                        </span>
                      </button>

                      <div
                        id={`cookies-section-${index}`}
                        className='lg-sec__body'
                        inert={!isOpen}
                      >
                        <div className='lg-sec__clip'>
                          <div className='lg-body'>{section.content}</div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </article>
          </div>
        </div>
      </section>

      <section
        className='sheet sheet--pad bafter bpage lg-after sheet--last'
        data-rail='Contact'
        style={{ '--bg': 'var(--paper-2)' } as CSSProperties}
      >
        <div className='container'>
          {/* Contact Section */}
          <div className='lg-contact'>
            <div className='lg-contact__head'>
              <Cookie aria-hidden='true' />
              <h2>Questions About Cookies?</h2>
              <p>
                We're happy to explain anything about what we store and why.
              </p>
            </div>
            <div className='lg-contact__cells'>
              <div className='lg-contact__cell'>
                <p className='lg-label'>Privacy Inquiries</p>
                <a href='mailto:privacy@pixelmojo.io' className='lg-biglink'>
                  privacy@pixelmojo.io
                </a>
              </div>
              <div className='lg-contact__cell'>
                <p className='lg-label'>General Contact</p>
                <a href='mailto:founders@pixelmojo.io' className='lg-biglink'>
                  founders@pixelmojo.io
                </a>
              </div>
            </div>
          </div>

          {/* Related Links */}
          <div className='lg-related'>
            <p>Related policies</p>
            <div className='hero__cta'>
              <Link href='/privacy-policy' className='btn'>
                Privacy Policy →
              </Link>
              <Link href='/terms-of-service' className='btn btn--ghost'>
                Terms of Service →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
